import React from 'react';
import { motion } from 'framer-motion';
import {
  Zap, Shield, Globe, Network, Users, Layers
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { whataLabLogo } from '../assets';
import './BlockchainFeatures.css';

/* ─── Supported Networks ─────────────────────────── */
const NETWORKS = [
  { name: 'Hedera', status: 'live' },
  { name: 'Solana', status: 'soon' },
  { name: 'Base', status: 'soon' },
  { name: 'Ethereum', status: 'soon' },
  { name: 'Arbitrum', status: 'soon' },
];
// Triplicar para scroll infinito continuo
const MARQUEE_NETWORKS = [...NETWORKS, ...NETWORKS, ...NETWORKS];

/* ─── Features data ─────────────────────────────── */
const FEATURES = {
  en: [
    {
      icon: Globe,
      title: 'Born on Hedera, built for all',
      desc: "Our tools start on Hedera's fast, low-cost network — then expand wherever your community or business needs to go.",
      pos: 'tl',
    },
    {
      icon: Network,
      title: 'One place, every chain',
      desc: 'Manage your assets, roles and verifications across multiple networks from a single dashboard. No jumping between apps.',
      pos: 'tm',
    },
    {
      icon: Zap,
      title: 'Move assets freely',
      desc: 'Bridge tokens between chains in minutes with WhataBridge. Your liquidity goes where you need it — fast.',
      pos: 'tr',
    },
    {
      icon: Shield,
      title: 'Same trust, every chain',
      desc: 'Consistent verification and security standards across all supported networks. One experience, no compromises.',
      pos: 'bl',
    },
    {
      icon: Users,
      title: 'Meet your people anywhere',
      desc: "Your community isn't on one chain anymore. Reach them wherever their wallets live — Hedera, Solana, Base, Ethereum.",
      pos: 'bm',
    },
    {
      icon: Layers,
      title: 'Complexity handled',
      desc: 'Cross-chain is hard. We absorb that complexity so you can focus on your community, your project, your business.',
      pos: 'br',
    },
  ],
  es: [
    {
      icon: Globe,
      title: 'Nacido en Hedera, construido para todas',
      desc: 'Nuestras herramientas nacen en la red rápida y económica de Hedera — y se expanden donde tu comunidad o negocio lo necesite.',
      pos: 'tl',
    },
    {
      icon: Network,
      title: 'Un lugar, todas las redes',
      desc: 'Gestiona tus activos, roles y verificaciones en múltiples redes desde un solo panel. Sin saltar entre aplicaciones.',
      pos: 'tm',
    },
    {
      icon: Zap,
      title: 'Mueve activos libremente',
      desc: 'Haz bridge de tokens entre cadenas en minutos con WhataBridge. Tu liquidez va donde la necesitas — rápido.',
      pos: 'tr',
    },
    {
      icon: Shield,
      title: 'Misma confianza, todas las redes',
      desc: 'Estándares de verificación y seguridad consistentes en todas las redes soportadas. Una experiencia, sin compromisos.',
      pos: 'bl',
    },
    {
      icon: Users,
      title: 'Encuentra a tu gente en cualquier lugar',
      desc: 'Tu comunidad ya no está en una sola red. Alcánzalos donde estén sus wallets — Hedera, Solana, Base, Ethereum.',
      pos: 'bm',
    },
    {
      icon: Layers,
      title: 'Complejidad resuelta',
      desc: 'Lo cross-chain es difícil. Nosotros absorbemos esa complejidad para que te enfoques en tu comunidad, tu proyecto, tu negocio.',
      pos: 'br',
    },
  ],
};

/* ─── Card ──────────────────────────────────────── */
const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <motion.div
      className={`bf-card bf-card--${feature.pos}`}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.45, delay: index * 0.07 }}
    >
      <div className="bf-card-icon">
        <Icon size={22} />
      </div>
      <div className="bf-card-body">
        <h3 className="bf-card-title">{feature.title}</h3>
        <p className="bf-card-desc">{feature.desc}</p>
      </div>
    </motion.div>
  );
};

/* ─── Main ──────────────────────────────────────── */
const BlockchainFeatures = () => {
  const { language } = useLanguage();
  const isEN = language === 'en';
  const features = FEATURES[language] || FEATURES.en;

  return (
    <section className="bf-section section" id="why-multichain">
      <div className="container">

        {/* Header */}
        <motion.div
          className="bf-header"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35 }}
        >
          <div className="bf-eyebrow">
            <span className="bf-eyebrow-dot" />
            {isEN ? 'why multichain' : 'por qué multichain'}
          </div>
          <h2 className="bf-title">
            {isEN
              ? <><span className="bf-title-accent">One home,</span><br />every chain.</>
              : <><span className="bf-title-accent">Un hogar,</span><br />todas las cadenas.</>
            }
          </h2>
        </motion.div>

        {/* Grid with center logo */}
        <div className="bf-grid">

          {/* Left column */}
          <div className="bf-col bf-col--left">
            {features.slice(0, 3).map((f, i) => (
              <FeatureCard key={f.pos} feature={f} index={i} />
            ))}
          </div>

          {/* Center: WhataLab logo hub */}
          <div className="bf-center">
            {/* Connection lines SVG */}
            <svg className="bf-lines" viewBox="0 0 200 600" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Lines from center to each card */}
              <line x1="100" y1="300" x2="0"   y2="100"  stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="100" y1="300" x2="0"   y2="300"  stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="100" y1="300" x2="0"   y2="500"  stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="100" y1="300" x2="200" y2="100"  stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="100" y1="300" x2="200" y2="300"  stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="100" y1="300" x2="200" y2="500"  stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
            </svg>

            {/* Hub logo */}
            <motion.div
              className="bf-hub"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.35, delay: 0.2 }}
            >
              <img src={whataLabLogo} alt="WhataLab" className="bf-hub-logo" />
              <span className="bf-hub-label">
                {isEN ? 'Hedera Origin' : 'Origen Hedera'}
              </span>
              <span className="bf-hub-chain">WhataLab</span>
            </motion.div>

            {/* Multichain pill below hub */}
            <motion.div
              className="bf-multichain"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.35, delay: 0.5 }}
            >
              <span className="bf-multichain-dot" />
              {isEN ? 'Multichain ready' : 'Listo para multichain'}
            </motion.div>
          </div>

          {/* Right column */}
          <div className="bf-col bf-col--right">
            {features.slice(3, 6).map((f, i) => (
              <FeatureCard key={f.pos} feature={f} index={i + 3} />
            ))}
          </div>

        </div>

        {/* Networks marquee — estilo council */}
        <motion.div
          className="bf-networks"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35, delay: 0.3 }}
        >
          <span className="bf-networks-label">
            {isEN ? 'Expanding to' : 'Expandiendo a'}
          </span>

          {/* Marquee — scroll infinito automático */}
          <div className="bf-marquee-wrap">
            <div className="bf-marquee-track">
              {MARQUEE_NETWORKS.map((network, i) => (
                <div 
                  key={`${network.name}-${i}`} 
                  className={`bf-marquee-item ${network.status === 'live' ? 'bf-marquee-item--live' : 'bf-marquee-item--soon'}`}
                >
                  <span className="bf-marquee-name">{network.name}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BlockchainFeatures;
